import { format, parseISO, isValid } from 'date-fns'
import {
  IFuelClaimResponse,
  IFuelDrumFloatResponse,
  IFuelReconciliationResponse,
} from 'types/api/fuelCustodyApi.interface'
import {
  IFuelMachineDetailResponse,
  IFuelMachineLeagueResponse,
} from 'types/api/fuelConsumptionApi.interface'
import {
  IBatch,
  IDispense,
  IFuelLossResponse,
  IFuelTank,
  IUserForFuelManagementResponse,
} from 'types/api/fuelManagementApi.interface'
import { FuelLossCaseStatus, FuelLossOutcome } from 'types/fuelEnums.interface'
import { IFuelConsumptionWindow, IFuelMachine, IFuelMachineDispense } from 'types/fuelConsumption.interface'
import { IFuelDeliveryRecord } from 'types/fuelDeliveries.interface'
import { IFuelHousekeepingRow } from 'types/fuelCustody.interface'
import { IFuelOpsDispenseRow, IFuelOpsLossRow, IFuelSupplierClaim, IFuelDrumFloat } from 'types/fuelOps.interface'
import { formatDateForDisplay, formatDateWithTime } from 'utils/helpers'
import {
  CLAIM_STATUS_TO_UI,
  DATA_MODE_TO_UI,
  DELIVERY_VERIFICATION_TO_UI,
  DISPENSE_AUTH_TO_UI,
  DISPENSE_METHOD_TO_UI,
  DRUM_STATUS_TO_UI,
  LOSS_CASE_STATUS_TO_UI,
  LOSS_OUTCOME_TO_UI,
  LOSS_ROOT_CAUSE_TO_UI,
  LOSS_SEVERITY_TO_UI,
  MACHINE_CLASS_TO_UI,
  RATE_PROVENANCE_TO_UI,
} from './fuelEnumMaps'
import { FUEL_UNVERIFIED_DELIVERY_NOTE } from './fuelFormatters'

export const mapFuelMachineLeague = (item: IFuelMachineLeagueResponse): IFuelMachine => ({
  id: item.id,
  name: item.name,
  aliases: [],
  model: item.model || '—',
  machineClass: item.machineClass != null ? MACHINE_CLASS_TO_UI[item.machineClass] : 'OPERATED_MOBILE',
  fuelDataMode: DATA_MODE_TO_UI[item.fuelDataMode],
  nominalBurnRateLph: item.nominalBurnRateLph ?? 0,
  tankCapacityL: item.tankCapacityL,
  fuelLevelPct: item.fuelLevelPct,
  telemetrySilentSince: item.telemetrySilentSince,
  telemetrySilentHours: item.telemetrySilentHours,
  litres30d: item.litres30d,
  avgRateLph: item.avgRateLph ?? 0,
  cost30dGhs: item.cost30dGhs,
  avgProvenance: RATE_PROVENANCE_TO_UI[item.avgProvenance],
  litresPerTonne: null,
  lastFillLabel: item.lastFill
    ? `${formatDateForDisplay(item.lastFill.at)} · ${item.lastFill.litres} L`
    : '—',
  trend7dLph: [],
  note: null,
  flags: item.flags.map((f) => ({
    kind: f.kind as IFuelMachine['flags'][0]['kind'],
    label: f.label,
    tone: f.tone === 'danger' ? 'danger' : 'warning',
  })),
  measuredSegments: [],
  fillWindows: [],
  dispenses: [],
  operators: [],
  tasks: [],
  runtimeDays: [],
  unexplainedEvents: [],
  daysToEmpty: null,
  nextRefillNote: null,
  alerts: [],
})

export const mapFuelMachineDetail = (item: IFuelMachineDetailResponse): IFuelMachine => {
  const base = mapFuelMachineLeague(item)
  return {
    ...base,
    fillWindows: item.fillWindows.map(mapConsumptionWindow),
    dispenses: item.dispenses.map(mapMachineDispense),
  }
}

export const mapConsumptionWindow = (
  w: IFuelMachineDetailResponse['fillWindows'][0],
): IFuelConsumptionWindow => {
  const fromLabel = w.from ? formatDateForDisplay(w.from) : '—'
  const toLabel = w.to ? formatDateForDisplay(w.to) : '—'
  return {
    id: w.id,
    periodLabel: `${fromLabel} → ${toLabel}`,
    hours: w.hours,
    litres: w.litres,
    ratePerHour: w.ratePerHour,
    provenance: RATE_PROVENANCE_TO_UI[w.provenance],
    context: '',
    unusable: w.unusable,
  }
}

export const mapMachineDispense = (
  d: IFuelMachineDetailResponse['dispenses'][0],
): IFuelMachineDispense => {
  const at = parseISO(d.at)
  return {
    id: d.id,
    dateLabel: formatDateForDisplay(d.at),
    time: format(at, 'HH:mm'),
    litres: d.litres,
    smuHours: d.smuHours,
    operator: d.operator || '—',
    station: d.station || '—',
  }
}

export const formatFuelUserName = (
  user: IUserForFuelManagementResponse | string | null | undefined,
): string => {
  if (!user) return '—'
  if (typeof user === 'string') return user || '—'
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  return name || '—'
}

export const mapFuelLoss = (item: IFuelLossResponse): IFuelOpsLossRow => {
  const severity =
    item.severity != null
      ? LOSS_SEVERITY_TO_UI[item.severity]
      : item.amount >= 1000
        ? 'critical'
        : 'warning'
  const caseStatus = LOSS_CASE_STATUS_TO_UI[item.caseStatus] || 'OPEN'
  const outcome = item.outcome != null ? LOSS_OUTCOME_TO_UI[item.outcome] : null
  let status: IFuelOpsLossRow['status'] = 'Under investigation'
  if (caseStatus === 'CLOSED') {
    status = outcome === 'WRITTEN_OFF' ? 'Written off — signed' : 'Explained'
  } else if (caseStatus === 'OPEN') {
    status = 'Under investigation'
  }
  return {
    id: item.id,
    dateLabel: formatDateForDisplay(item.date),
    liters: item.litres ?? item.amount,
    tank: item.tank?.name || '—',
    classification: item.classification || '—',
    severity,
    owner: formatFuelUserName(item.owner),
    ownerUserId: item.owner?.id ?? null,
    status,
    note: item.investigationNote || undefined,
    caseStatus,
    outcome,
    rootCause: item.rootCause != null ? LOSS_ROOT_CAUSE_TO_UI[item.rootCause] : null,
    closedAt: item.closedAt ? formatDateForDisplay(item.closedAt) : undefined,
    closedBy: item.closedBy ? formatFuelUserName(item.closedBy) : null,
    approval: item.approval
      ? {
          requiredAboveGhs: item.approval.thresholdGhs,
          approverRole: item.approval.approverRole,
          approvedBy: item.approval.approvedBy
            ? formatFuelUserName(item.approval.approvedBy)
            : undefined,
          approvedAt: item.approval.approvedAt
            ? formatDateForDisplay(item.approval.approvedAt)
            : undefined,
        }
      : undefined,
    costGhs: item.costGhs ?? undefined,
  }
}

export const fuelLossInvestigationLabel = (item: IFuelLossResponse): string => {
  if (item.caseStatus === FuelLossCaseStatus.Closed) {
    return item.outcome === FuelLossOutcome.WrittenOff ? 'Written off — signed' : 'Explained'
  }
  if (item.caseStatus === FuelLossCaseStatus.Open) return 'Unassigned'
  if (item.caseStatus === FuelLossCaseStatus.PendingApproval) return 'Pending approval'
  return 'Under investigation'
}


export const mapFuelClaim = (item: IFuelClaimResponse): IFuelSupplierClaim => ({
  id: item.id,
  raisedLabel: formatDateForDisplay(item.raisedAt),
  supplier: item.supplier || '—',
  deliveryRef: item.deliveryRef || `Delivery #${item.deliveryId}`,
  litresShort: item.litresShort,
  valueGhs: item.valueGhs,
  ageDays: item.ageDays,
  status: CLAIM_STATUS_TO_UI[item.status],
  note: item.note || '',
})

export const mapBatchToDelivery = (b: IBatch): IFuelDeliveryRecord => ({
  id: b.id,
  dateLabel: formatDateWithTime(b.date),
  liters: b.quantity,
  costGhs: b.cost || 0,
  supplier: b?.supplier?.name || '—',
  tank: b?.fuelTank?.name || '—',
  fuelType: b?.fuelType?.name || '—',
  pricePerL: b.pricePerLitre ?? (b.cost && b.quantity ? Math.round((b.cost / b.quantity) * 100) / 100 : null),
  waybillRef: b.waybillRef ?? null,
  waybillLitres: b.waybillLitres ?? null,
  tankDipBeforeL: b.tankDipBeforeL ?? null,
  tankDipAfterL: b.tankDipAfterL ?? null,
  measuredInL: b.measuredInL ?? null,
  varianceL: b.varianceL ?? null,
  variancePct: b.variancePct ?? null,
  sealNumbers: b.sealNumbers ?? [],
  sealsIntact: b.sealsIntact ?? null,
  verifiedBy: b.verifiedBy ? `${b.verifiedBy.firstName} ${b.verifiedBy.lastName}` : null,
  verificationStatus:
    b.verificationStatus != null
      ? DELIVERY_VERIFICATION_TO_UI[b.verificationStatus]
      : 'UNVERIFIED',
  verificationNote: b.verificationNote ?? FUEL_UNVERIFIED_DELIVERY_NOTE,
  receivedBy: b.receivedBy ?? (b.user ? `${b.user.firstName} ${b.user.lastName}` : null),
  claimId: b.claimId ?? null,
})

export const mapDispenseToRow = (d: IDispense): IFuelOpsDispenseRow => ({
  id: d.id,
  dayLabel: formatDateForDisplay(d.dispensedAt),
  time: d.dispensedAt && isValid(new Date(d.dispensedAt)) ? format(new Date(d.dispensedAt), 'HH:mm') : '—',
  machine: d.machine?.name || (d.toFuelTank ? `→ ${d.toFuelTank.name}` : '—'),
  machineId: d.machine?.id,
  operator: d?.user ? `${d.user.firstName} ${d.user.lastName}` : '—',
  liters: d.quantity,
  smuHours: d.engineHours ?? null,
  tank: d.fromFuelTank?.name || '—',
  dispenseType: d.dispenseType != null ? DISPENSE_METHOD_TO_UI[d.dispenseType] : 'MACHINE',
  authStatus: d.authStatus != null ? DISPENSE_AUTH_TO_UI[d.authStatus] : undefined,
  signatureRef: d.signatureRef ?? undefined,
  contractor: d.contractor ?? undefined,
  costGhs: d.costGhs ?? d.cost ?? undefined,
  flag:
    d.dataQualityFlag === 1
      ? { tone: 'warning', label: 'Missing SMU' }
      : d.authStatus === 2
        ? { tone: 'danger', label: 'Unauthorized draw' }
        : undefined,
})

export const mapFuelDrum = (item: IFuelDrumFloatResponse): IFuelDrumFloat => ({
  id: item.id,
  ref: item.ref || `DRM-${item.id}`,
  issuedTo: item.issuedTo || '—',
  issuedL: item.issuedL,
  issuedLabel: formatDateForDisplay(item.issuedAt),
  confirmedUsedL: item.confirmedUsedL,
  confirmedVia: item.confirmedVia || '—',
  returnedEmptyL: item.returnedEmptyL,
  floatL: item.floatL,
  status: DRUM_STATUS_TO_UI[item.status],
  note: item.note || undefined,
})

export const mapReconToCustodyDay = (item: IFuelReconciliationResponse) => ({
  tankId: item.tankId,
  dateISO: item.dateISO,
  openingDipL: item.openingDipL,
  closingDipL: item.closingDipL,
  deliveriesL: item.deliveriesL,
  dispensesL: item.dispensesL,
  dispenseCount: item.dispenseCount,
  transfers: item.transfers.map((t) => ({
    id: t.id,
    direction: t.direction === 1 ? 'TRANSFER_IN' : 'TRANSFER_OUT',
    counterparty: t.counterparty,
    litres: t.litres,
    time: t.time,
    label: t.label,
  })),
  reconId: item.id,
  status: item.status,
  signOff: item.signOff,
  withinTolerance: item.withinTolerance,
  varianceL: item.varianceL,
  variancePct: item.variancePct,
  bookStockL: item.bookStockL,
  tolerancePct: item.tolerancePct,
  transfersInL: item.transfersInL,
  transfersOutL: item.transfersOutL,
})

export const mapTankHousekeeping = (tank: IFuelTank): IFuelHousekeepingRow | null => {
  const hk = tank.housekeeping
  if (!hk) return null
  return {
    id: tank.id,
    tank: tank.name,
    lastCleaned: hk.lastCleanedAt ? formatDateForDisplay(hk.lastCleanedAt) : '—',
    nextDue: hk.nextCleanDueAt ? formatDateForDisplay(hk.nextCleanDueAt) : '—',
    waterHistory: hk.waterHistory?.length ? hk.waterHistory.join(' · ') : '—',
    statusTone: hk.overdue ? 'danger' : 'success',
    statusLabel: hk.overdue ? 'Overdue' : 'Ok',
  }
}
